import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { grabService, ProductPlatformMapping } from '../services/grabService';
import { productsService } from '../services/productsService';
import { Product } from '../types';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/useToast';

// Lucide React icons
import {
  Plus,
  Edit,
  Trash2,
  Search,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  ExternalLink,
} from 'lucide-react';

interface ProductMappingListProps {
  onCreateClick: () => void;
  onEditClick: (mapping: ProductPlatformMapping) => void;
  refreshTrigger?: number;
}

export const ModernProductMappingList: React.FC<ProductMappingListProps> = ({
  onCreateClick,
  onEditClick,
  refreshTrigger,
}) => {
  const { translate } = useTranslation();
  const { success, error: showError } = useToast();
  const [mappings, setMappings] = useState<ProductPlatformMapping[]>([]);
  const [products, setProducts] = useState<Record<string, Product>>({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSyncEnabled, setFilterSyncEnabled] = useState<
    'all' | 'enabled' | 'disabled'
  >('all');
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const loadMappings = async () => {
    try {
      setLoading(true);
      const params: any = { platform: 'grab' };
      if (filterSyncEnabled !== 'all') {
        params.syncEnabled = filterSyncEnabled === 'enabled';
      }
      const data = await grabService.getMappings(params);
      setMappings(data);

      // Load products
      const productIds = Array.from(new Set(data.map((m) => m.productId)));
      const productsData = await Promise.all(
        productIds.map(async (id) => {
          try {
            const product = await productsService.getProduct(id);
            return [id, product];
          } catch {
            return [id, null];
          }
        })
      );
      const productsMap: Record<string, Product> = {};
      productsData.forEach(([id, product]) => {
        if (product) {
          // Convert API product to local Product type
          const apiProduct = product as unknown as any;
          const localProduct: Product = {
            id: apiProduct.id,
            categoryId: apiProduct.categoryId || '',
            description: apiProduct.description || '',
            height: apiProduct.height || 0,
            width: apiProduct.width || 0,
            image: apiProduct.image || '',
            price: apiProduct.price || 0,
            reference: apiProduct.reference || '',
            stock: apiProduct.stock || 0,
            thumbnail: apiProduct.thumbnail || '',
          };
          productsMap[id as string] = localProduct;
        }
      });
      setProducts(productsMap);
    } catch (error: any) {
      showError(error.message || 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMappings();
  }, [refreshTrigger, filterSyncEnabled]);

  const handleDelete = async (id: string) => {
    if (!confirm(translate('pos.grab.mappings.delete_confirm', 'Are you sure you want to delete this mapping?'))) return;

    try {
      await grabService.deleteMapping(id);
      success('Mapping deleted successfully');
      loadMappings();
    } catch (error: any) {
      showError(error.message || 'Failed to delete mapping');
    }
  };

  const handleToggleSync = async (mapping: ProductPlatformMapping) => {
    try {
      if (mapping.syncEnabled) {
        await grabService.disableSync(mapping.id);
      } else {
        await grabService.enableSync(mapping.id);
      }
      success(
        `Sync ${mapping.syncEnabled ? 'disabled' : 'enabled'} successfully`
      );
      loadMappings();
    } catch (error: any) {
      showError(error.message || 'Failed to toggle sync');
    }
  };

  const handleSyncProduct = async (mapping: ProductPlatformMapping) => {
    try {
      setSyncingId(mapping.id);
      await grabService.syncProduct(mapping.productId);
      success('Product synced successfully');
      loadMappings();
    } catch (error: any) {
      showError(error.message || 'Failed to sync product');
    } finally {
      setSyncingId(null);
    }
  };

  const filteredMappings = mappings.filter((mapping) => {
    const product = products[mapping.productId];
    const productName = product?.reference || '';
    return (
      productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mapping.platformItemId.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const getStatusBadge = (mapping: ProductPlatformMapping) => {
    if (!mapping.syncEnabled) {
      return <Badge variant="secondary">Disabled</Badge>;
    }

    switch (mapping.syncStatus) {
      case 'synced':
        return (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Synced
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="secondary">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Product Platform Mappings</h2>
          <p className="text-muted-foreground">
            Manage product mappings to Grab platform
          </p>
        </div>
        <Button onClick={onCreateClick}>
          <Plus className="w-4 h-4 mr-2" />
          Create Mapping
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by product name or Grab item ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <Select
              value={filterSyncEnabled}
              onValueChange={(value: any) => setFilterSyncEnabled(value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by sync status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Mappings</SelectItem>
                <SelectItem value="enabled">Sync Enabled</SelectItem>
                <SelectItem value="disabled">Sync Disabled</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadMappings}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Mappings ({filteredMappings.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : filteredMappings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No mappings found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Grab Item ID</TableHead>
                  <TableHead>Grab Category ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Synced</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMappings.map((mapping) => {
                  const product = products[mapping.productId];
                  return (
                    <TableRow key={mapping.id}>
                      <TableCell>
                        {product?.reference || mapping.productId}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {mapping.platformItemId}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {mapping.platformCategoryId || '-'}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(mapping)}
                        {mapping.syncError && (
                          <div className="text-xs text-red-500 mt-1">
                            {mapping.syncError}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {mapping.lastSyncedAt
                          ? new Date(mapping.lastSyncedAt).toLocaleString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSyncProduct(mapping)}
                            disabled={
                              syncingId === mapping.id || !mapping.syncEnabled
                            }
                          >
                            {syncingId === mapping.id ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3 h-3" />
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleSync(mapping)}
                          >
                            {mapping.syncEnabled ? 'Disable' : 'Enable'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onEditClick(mapping)}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(mapping.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
